import {
  AsyncStorageProvider,
  StorageProvider,
} from "@quotientjs/browser-storage"

import { DeviceIdStore, DeviceId } from "@/stores/device-id"
import { SessionIdStore, SessionId } from "@/stores/session-id"
import { PersonIdStore, PersonId } from "@/stores/person-id"
import {
  BrowserFingerprintStore,
  BrowserFingerprint,
} from "@/stores/browser-fingerprint"

/**
 * Store client that provides access to commonly used storage values
 */
export class Store {
  private provider: StorageProvider | AsyncStorageProvider

  constructor(provider: StorageProvider | AsyncStorageProvider) {
    this.provider = provider
  }

  /**
   * Get the device ID
   * @returns The device ID or null if not set
   */
  deviceId(): DeviceId | null {
    if (this.provider.type !== "sync") {
      throw new Error("async operation called on sync storage provider")
    }
    return DeviceIdStore.get(this.provider as StorageProvider)
  }

  async deviceIdAsync(): Promise<DeviceId | null> {
    if (this.provider.type !== "async") {
      return DeviceIdStore.get(this.provider as StorageProvider)
    }
    return DeviceIdStore.getAsync(this.provider as AsyncStorageProvider)
  }

  /**
   * Get the session ID
   * @returns The session ID or null if not set
   */
  sessionId(): SessionId | null {
    if (this.provider.type !== "sync") {
      throw new Error("async operation called on sync storage provider")
    }
    return SessionIdStore.get(this.provider as StorageProvider)
  }

  async sessionIdAsync(): Promise<SessionId | null> {
    if (this.provider.type !== "async") {
      return SessionIdStore.get(this.provider as StorageProvider)
    }
    return SessionIdStore.getAsync(this.provider as AsyncStorageProvider)
  }

  /**
   * Get the browser fingerprint
   * @returns The browser fingerprint or null if not set
   */
  browserFingerprint(): BrowserFingerprint | null {
    if (this.provider.type !== "sync") {
      throw new Error("async operation called on sync storage provider")
    }
    return BrowserFingerprintStore.get(this.provider as StorageProvider)
  }

  async browserFingerprintAsync(): Promise<BrowserFingerprint | null> {
    if (this.provider.type !== "async") {
      return BrowserFingerprintStore.get(this.provider as StorageProvider)
    }
    return BrowserFingerprintStore.getAsync(
      this.provider as AsyncStorageProvider,
    )
  }

  /**
   * Get the person ID
   * @returns The person ID or null if not set
   */
  personId(): PersonId | null {
    if (this.provider.type !== "sync") {
      throw new Error("async operation called on sync storage provider")
    }
    return PersonIdStore.get(this.provider as StorageProvider)
  }

  async personIdAsync(): Promise<PersonId | null> {
    if (this.provider.type !== "async") {
      return PersonIdStore.get(this.provider as StorageProvider)
    }
    return PersonIdStore.getAsync(this.provider as AsyncStorageProvider)
  }

  /**
   * Clear all stored data from all storage types
   * Completely wipes all client state including device ID, session ID, etc.
   */
  clear(): void {
    if (this.provider.type !== "sync") {
      throw new Error("async operation called on sync storage provider")
    }
    // Clear all storage types
    this.provider.clear("local")
    this.provider.clear("session")
    this.provider.clear("cookie")
  }

  async clearAsync(): Promise<void> {
    if (this.provider.type !== "async") {
      throw new Error("sync operation called on async storage provider")
    }
    // Clear all storage types
    await this.provider.clear("local")
    await this.provider.clear("session")
    await this.provider.clear("cookie")
  }
}
